import { AmbientLight, BoxGeometry, CameraHelper, Color, DirectionalLight, Fog, FogExp2, Intersection, Mesh, MeshBasicMaterial, MeshLambertMaterial, Object3D, PCFSoftShadowMap, PerspectiveCamera, PMREMGenerator, Raycaster, Scene, Sphere, SphereGeometry, sRGBEncoding, Vector2, Vector3, WebGLRenderer } from "three";
import { BaseElement, BaseElementOptions } from "../display/BaseElement.js";
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { GlitchPass } from 'three/examples/jsm/postprocessing/GlitchPass.js';
import { BokehPass } from 'three/examples/jsm/postprocessing/BokehPass.js';
import { SSAOPass } from 'three/examples/jsm/postprocessing/SSAOPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { BokehShader, BokehDepthShader } from 'three/examples/jsm/shaders/BokehShader2.js';
import { PointerInfoClick } from "../../../../../eventfive.core-v2/types/eventfive.core-v2.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { TransformControls } from "three/examples/jsm/controls/TransformControls.js";
import { Control3d } from "./Control3d.js";

export interface Canvas3dOptions extends BaseElementOptions {

    bloom?: boolean;
    ssao?: boolean;
    gui: dat.GUI;
    cameraPosition: Vector3;
}

export class Canvas3d {

    public readonly onUpdate = e5.Signal.create();
    public readonly onAfterUpdate = e5.Signal.create();
    public readonly onResize: e5.ResizeSignal;

    public readonly element: HTMLElement;
    public readonly scene: Scene;
    public readonly renderer: WebGLRenderer;
    public readonly composer: EffectComposer;
    public readonly camera: PerspectiveCamera;
    public readonly canvas: HTMLCanvasElement;

    private _viewport: HTMLElement;

    private readonly _mutable: {
        -readonly [k in keyof this]: this[k];
    } = this;

    private _raycaster: Raycaster = new Raycaster();

    public constructor(options: Canvas3dOptions) {
        this.element = document.createElement("div");
        this.element.className = "awi-canvas3d awi-canvas3d--hidden";
        options.container.appendChild(this.element);

        this.onResize = e5.Resize.create(this.element);
        this.onResize.add(() => this.resize());

        this._mutable = this;

        var width = this.element.offsetWidth;
        var height = this.element.offsetHeight;

        this._mutable.camera = new PerspectiveCamera(50, width / height, 0.1, 1500);
        this.camera.position.copy(options.cameraPosition);
        this.camera.zoom = 1;

        this._mutable.scene = new Scene();

        this._mutable.renderer = new WebGLRenderer({ antialias: true, alpha: false, logarithmicDepthBuffer: false });
        this.renderer.setClearAlpha(0);
        this.renderer.autoClear = false;
        this.renderer.shadowMap.enabled = true;
        this.renderer.physicallyCorrectLights = true;
        this.renderer.outputEncoding = sRGBEncoding;
        this.renderer.domElement.classList.add("awi-canvas3d__canvas");
        // this.renderer.shadowMap.type = PCFSoftShadowMap;
        let pixelRatio = Math.max(window.devicePixelRatio || 2, 2);

        // pixelRatio = 1;

        this.renderer.setPixelRatio(pixelRatio);

        this._mutable.composer = new EffectComposer(this.renderer);
        this.composer.setPixelRatio(pixelRatio);

        const renderPass = new RenderPass(this.scene, this.camera);
        this.composer.addPass(renderPass);

        if (options.ssao) {
            const ssaoPass = new SSAOPass(this.scene, this.camera);
            this.composer.addPass(ssaoPass);
        }

        if (options.bloom) {
            const params = {
                exposure: 1,
                bloomStrength: 0.15,
                bloomThreshold: 0,
                bloomRadius: 0
            };
            const bloomPass = new UnrealBloomPass(new Vector2(window.innerWidth, window.innerHeight), 1.5, 0.4, 0.85);
            bloomPass.threshold = params.bloomThreshold;
            bloomPass.strength = params.bloomStrength;
            bloomPass.radius = params.bloomRadius;
            this.composer.addPass(bloomPass);
        }

        this.renderer.setAnimationLoop((time: number) => this.animation(time));

        this.element.appendChild(this.renderer.domElement);
        this._mutable.canvas = this.renderer.domElement;

        this._viewport = document.createElement("div");
        this._viewport.className = "canvas3d__viewport";
        this.element.appendChild(this._viewport);

        var light = new DirectionalLight(0xffffff, 2.5);
        light.position.set(2, 100, 100);
        this.scene.add(light);
        var gui = options.gui.addFolder("Directional Light");
        gui.add(light, "intensity", 0, 5);

        // light.castShadow = true;
        // light.shadow.mapSize.width = 512;
        // light.shadow.mapSize.height = 512;
        // light.shadow.camera.near = 5;
        // light.shadow.camera.far = 1500;
        // light.shadow.camera.left = -200;
        // light.shadow.camera.right = 200;
        // light.shadow.camera.top = -200;
        // light.shadow.camera.bottom = 200;

        // const helper = new CameraHelper(light.shadow.camera);
        // this.scene.add(helper);

        const amb = new AmbientLight(0xFFFFFF, 1.5);
        this.scene.add(amb);
        var gui = options.gui.addFolder("Ambient Light");
        gui.add(amb, "intensity", 0, 5);

        // let pmremGenerator = new PMREMGenerator(this.renderer);
        // pmremGenerator.compileEquirectangularShader();

        // const density = 0.1;
        // this.scene.fog = new FogExp2(color, density);
        // this.addFog();

        this.resize();

        return this;
    }

    private addFog(): void {
        // const color = 0xaeecd5;
        const color = 0xFFFFFF;  // white
        const near = 5;
        const far = 300;
        this.scene.fog = new Fog(color, near, far);
    }


    public local3dToGlobal2d(v: Vector3): Vector2 {

        var width = this.renderer.domElement.offsetWidth;
        var height = this.renderer.domElement.offsetHeight;
        var widthHalf = width / 2;
        var heightHalf = height / 2;

        v = v.clone();
        v.project(this.camera);

        return new Vector2(
            (v.x * widthHalf) + widthHalf,
            - (v.y * heightHalf) + heightHalf)
    }

    public resize(): void {

        var width = this.element.offsetWidth;
        var height = this.element.offsetHeight;

        var size = this.renderer.getSize(new Vector2());
        if (width == size.x
            && height == size.y) {
            // console.log("NO RESIZE NEEDED!");
            return;
        }

        var aspect = width / height;

        this.camera.aspect = aspect;

        var fov = 70;
        var baseWidth = 800;
        var tanFOV = Math.tan(((Math.PI / 180) * fov / 2));
        this.camera.fov = (360 / Math.PI) * Math.atan(tanFOV * (height / baseWidth));

        this.updateOffset();

        this.renderer.setSize(width, height);
        this.composer.setSize(width, height);

        this.render();
    }

    private _offsetX: number = 0;
    private _offsetXDirty: boolean = true;

    public get offsetX(): number {
        return this._offsetX;
    }
    public set offsetX(value: number) {
        if (this._offsetX == value) {
            return;
        }
        this._offsetX = value;
        this._offsetXDirty = true;

    }

    private _offsetY: number = 0;
    private _offsetYDirty: boolean = true;

    public get offsetY(): number {
        return this._offsetY;
    }
    public set offsetY(value: number) {
        if (this._offsetY == value) {
            return;
        }
        this._offsetY = value;
        this._offsetYDirty = true;

    }

    private animation(time: number) {
        this.render();
    }

    private render(): void {
        // this.camera.updateMatrixWorld();



        this.onUpdate.dispatch();
        // this.renderer.render(this.scene, this.camera);

        this.updateOffset();

        // if (this._offsetXDirty
        //     || this._offsetYDirty) {
        //     this._offsetXDirty = false;
        //     this._offsetYDirty = false;
        //     this.updateOffset();
        // }

        this.onAfterUpdate.dispatch();

        this.composer.render();
    }

    public get viewportActive(): boolean {
        return this._viewport.classList.contains("canvas3d__viewport--active");
    }

    public set viewportActive(value: boolean) {
        this._viewport.classList.toggle("canvas3d__viewport--active", value);
    }

    private updateOffset(): void {

        var width = this.element.offsetWidth;
        var height = this.element.offsetHeight;
        const pixelRatio = this.renderer.getPixelRatio();

        let offX = this._viewport.offsetLeft + this._viewport.offsetWidth * 0.5;
        let offY = this._viewport.offsetTop + this._viewport.offsetHeight * 0.5;
        offX -= this.element.offsetWidth * 0.5;
        offY -= this.element.offsetHeight * 0.5;
        this.camera.setViewOffset(width, height, -offX, -offY, width, height);

        // this.camera.setViewOffset(width, height, -this._offsetX * 0.5, -this._offsetY * 0.5, width, height);

        this.camera.updateProjectionMatrix();
    }

    public toGlobal(position: Vector3): { x: number; y: number } {
        var pos = position.clone();
        pos.project(this.camera);
        return { x: (pos.x + 1) * this.canvas.width * 0.5, y: (-pos.y + 1) * this.canvas.height * 0.5 };
    }

    public hitTest(clientX: number, clientY: number, elements: Object3D[], recursive: boolean = false): Intersection[] {
        var local = this.toLocal(clientX, clientY);
        var x = local.x;
        var y = local.y;
        //Transform to NDC (Normal Device Coordinates).
        x = (x / this.canvas.offsetWidth) * 2 - 1;
        y = - (y / this.canvas.offsetHeight) * 2 + 1;
        this._raycaster.setFromCamera({ x: x, y: y }, this.camera);
        var intersects = this._raycaster.intersectObjects(elements, recursive);
        if (intersects.length > 0)
            return intersects;
        return null;
    }

    private toLocal(clientX: number, clientY: number): { x: number; y: number } {
        var bounds = this.canvas.getBoundingClientRect();
        return { x: clientX - bounds.left, y: clientY - bounds.top };
    }

    public get visible(): boolean {
        return !this.element.classList.contains("awi-canvas3d--hidden");
    }

    public set visible(value: boolean) {
        this.element.classList.toggle("awi-canvas3d--hidden", !value);
    }
}